/* eslint-disable prettier/prettier */
import { Field, ImageField, Image, Item, Text, useSitecoreContext, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import classNames from 'classnames/bind';
import React from 'react';
import type { ComponentProps } from 'lib/component-props';
import withStyles from 'lib/enhancers/withStyles';
import type { ResponsiveImageFields } from 'models/responsive-image';

import styles from './BioDetail.module.scss';
import { isNullOrEmptyObject } from 'lib/utils';
import Icon from 'components/helpers/Icon';

type BioDetailFields = ResponsiveImageFields & {
  name: Field<string>;
  bio: Field<string>;
  fallbackImage: ImageField;
  quote: Field<string>;
  image: ImageField;
  experience: Field<string>;
  bioTitles: Item[];
  phoneNumber: Field<string>;
  faxNumber: Field<string>;
  emailAddress: Field<string>;
  educationAndTraining: Field<string>;
  certifications: Item[];
  licenses: Item[];
  languagesSpoken: Item[];
  tty: Field<string>;
};

type BioDetailProps = ComponentProps & {
  fields: BioDetailFields;
};

const cx = classNames.bind(styles);

const BioDetail = (props: BioDetailProps): JSX.Element => {
  const { className } = props;
  const sendEmailRef = props.fields?.emailAddress?.value
    ? `mailto:${props.fields.emailAddress.value.replace(/<(.|\n)*?>/g, '')}`
    : '';
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;
  const { t } = useI18n();
  const displayPhotoFromPersonData = props.params?.displayPhotoFromPersonData === '1';

  const bioImage = props.fields?.image?.value?.src ? props.fields?.image : props.fields?.fallbackImage;

  if (isNullOrEmptyObject(props.fields)) {
    if (pageEditing) {
      return (
        <div className={cx('bio-detail', 'component-container-edge', className)}>
          <div className={cx('bio-detail-image')}>
            <Icon variant="error" className="text-warning mx-auto w-50 h-50"></Icon>
          </div>
          <div className={cx('bio-detail-content')}>
            <p>{t('Missing-BioDetail-Datasource')}</p>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }

  return (
    <>
      <div className={cx('bio-detail', 'component-container-edge', className)}>
        {displayPhotoFromPersonData && bioImage != null && (
          <div className={cx('bio-detail-image')}>
            <Image field={bioImage} imageParams={{ mw: 300, mh: 300 }} />
          </div>
        )}
        <div className={cx('bio-detail-content')}>
          {(props.fields?.name?.value ||
            props.fields?.bioTitles?.length > 0 ||
            props.fields?.experience?.value ||
            props.fields?.emailAddress?.value ||
            props.fields?.phoneNumber?.value ||
            sitecoreContext.pageEditing) && (
              <section>
                <h2>
                  <Text field={props.fields?.name} encode={sitecoreContext.pageEditing} />
                </h2>
                {props.fields?.bioTitles?.length > 0 &&
                  props.fields?.bioTitles?.map((titleItem, index) => (
                    <h4 key={index}>
                      <Text
                        field={titleItem.fields?.Value as Field<string>}
                        editable={false}
                        encode={sitecoreContext.pageEditing}
                      />
                    </h4>
                  ))}
                {(props.fields?.experience.value || sitecoreContext.pageEditing) && (
                  <h5>
                    <span className={cx('fw-bold')}>{t('BioDetailExperience')}:&nbsp;</span>
                    <span className={cx('fw-normal')}>
                      <Text field={props.fields?.experience} encode={sitecoreContext.pageEditing} />
                    </span>
                  </h5>
                )}
                {props.fields?.languagesSpoken?.length > 0 && (
                  <h5>
                    <span className={cx('fw-bold')}>{t('BioDetailLanguagesSpoken')}:&nbsp;</span>
                    {props.fields?.languagesSpoken?.map((langItem, index) => (
                      <span className={cx('fw-normal')} key={index}>
                        <Text
                          field={langItem.fields?.Value as Field<string>}
                          editable={false}
                          encode={sitecoreContext.pageEditing}
                        />
                        {props.fields.languagesSpoken.length > index + 1 && ', '}
                      </span>
                    ))}
                  </h5>
                )}
                {(props.fields?.phoneNumber.value || sitecoreContext.pageEditing) && (
                  <h5>
                    <span className={cx('fw-bold')}>{t('BioDetailPhone')}:&nbsp;</span>
                    <Text field={props.fields?.phoneNumber} encode={sitecoreContext.pageEditing} />
                    {(props.fields?.tty.value || sitecoreContext.pageEditing) && (
                      <span className={cx('tty')}>
                        {t('BioDetailTTYLabel')}: <Text field={props.fields?.tty}></Text>
                      </span>
                    )}
                  </h5>
                )}
                {(props.fields?.faxNumber.value || sitecoreContext.pageEditing) && (
                  <h5>
                    <span className={cx('fw-bold')}>{t('BioDetailFax')}:&nbsp;</span>
                    <Text field={props.fields?.faxNumber} encode={sitecoreContext.pageEditing} />
                  </h5>
                )}
                <h5>
                  {sitecoreContext.pageEditing ? (
                    <Text field={props.fields?.emailAddress} encode={sitecoreContext.pageEditing} />
                  ) : (
                    <a href={sendEmailRef}>
                      <Text field={props.fields?.emailAddress} encode={sitecoreContext.pageEditing} />
                    </a>
                  )}
                </h5>
              </section>
            )}
          {(props.fields?.bio?.value || sitecoreContext.pageEditing) && (
            <RichText tag="section" field={props.fields?.bio} />
          )}
          {(props.fields?.quote?.value || sitecoreContext.pageEditing) && (
            <section>
              <q>
                <Text field={props.fields?.quote} encode={sitecoreContext.pageEditing} />
              </q>
            </section>
          )}
          {(props.fields?.educationAndTraining?.value || sitecoreContext.pageEditing) && (
            <section>
              <h5>{t('BioDetailEducation')}:</h5>
              <ul>
                {props.fields?.educationAndTraining?.value.split(/\r?\n/).map((education, index) => (
                  <li key={index}>{<Text field={{ value: education }} encode={sitecoreContext.pageEditing} />}</li>
                ))}
              </ul>
            </section>
          )}
          {(props.fields?.certifications?.length > 0 || sitecoreContext.pageEditing) && (
            <section>
              <h5>{t('BioDetailCertification')}:</h5>
              <ul>
                {props.fields?.certifications?.map((certificationItem, index) => (
                  <li key={index}>
                    <Text
                      field={certificationItem.fields?.Value as Field<string>}
                      editable={false}
                      encode={sitecoreContext.pageEditing}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {(props.fields?.licenses?.length > 0 || sitecoreContext.pageEditing) && (
            <section>
              <h5>{t('BioDetailLicenses')}:</h5>
              <ul>
                {props.fields?.licenses?.map((licensesItem, index) => (
                  <li key={index}>
                    <Text
                      field={licensesItem.fields?.Value as Field<string>}
                      editable={false}
                      encode={sitecoreContext.pageEditing}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default withStyles()<BioDetailProps>(BioDetail);
